
import {processBarCode, removeStringFromBarcode} from './scanBarcodeFunctions';

const norm = value => (value == null ? '' : String(value).trim().toUpperCase());

// GROOV-4994 (defect 4): apply the same tenant barcode cleanup the rest of the app uses
// before classifying a box scan. Mirrors scanpack_item.js's boxChampEarlySetAside, which
// is the existing correct precedent for this exact pair of settings:
//   if (sps.escape_string_enabled) code = processBarCode(code, sps).barCodeWithoutSpaces;
//   if (sps.string_removal_enabled && sps.string_removal) code = removeStringFromBarcode(...)
// Currently unreachable in production (all BoxChamp tenants have box_scan_verification=0),
// but it becomes a hard floor-stop for a barcode-cleanup tenant the day that changes.
const cleanScannedBarcode = (value, settings = {}) => {
  let code = value == null ? '' : String(value).trim();
  if (settings.escape_string_enabled) {
    code = processBarCode(code, settings).barCodeWithoutSpaces;
  }
  if (settings.string_removal_enabled && settings.string_removal) {
    code = removeStringFromBarcode(code, settings.string_removal);
  }
  return code;
};

// GROOV-4994 (hand-off timer): this file used to export NEXT_BOX_CODE alongside
// classifyBoxScan, which is why it never tripped this rule before — a genuinely single
// named export now, kept named (not default) to match every other call site's existing
// `import {classifyBoxScan} from './boxChampScanRouter'` shape (boxChampHandoff.js,
// boxChampSubstitution.js) rather than churn two unrelated files for a style nit.
// eslint-disable-next-line import/prefer-default-export
export function classifyBoxScan(
  value,
  {expectedBox, catalog, pendingSubstitution, settings} = {},
) {
  // GROOV-4994 (hand-off timer): the guided flow used to have its own separate
  // "NEXTBOX" card barcode here (classified as kind 'next_box_code') alongside the
  // BOXNEXT/BOXPREV action barcodes multi-box navigation already uses elsewhere
  // (sharedValidationUtils.js's isActionCode, barcodeClassifiers.js). Per Dan: "I don't
  // see any reason to have two different sets of these barcodes... I think we can do
  // everything with the box next and box prev barcode." The unified BOXNEXT handling
  // now lives entirely in boxChampHandoff.js's classifyPausedInput (checked BEFORE this
  // function is ever called, and short-circuited via isActionCode's pass-through when a
  // box scan is not required) — there is nothing left for classifyBoxScan itself to do
  // with the action barcode, so it now falls through to the ordinary catalog lookup
  // below like any other unrecognized value (kind 'none').
  const scanned = norm(settings ? cleanScannedBarcode(value, settings) : value);
  if (!scanned) return {kind: 'none'};

  const expectedBarcode = expectedBox && expectedBox.barcode ? norm(expectedBox.barcode) : null;

  const boxType = (Array.isArray(catalog) ? catalog : [])
    .find(bt => bt && bt.barcode && norm(bt.barcode) === scanned);
  if (!boxType) {
    if (expectedBarcode && scanned === expectedBarcode) {
      return {kind: 'confirm_expected', boxType: expectedBox};
    }
    return {kind: 'none'};
  }

  if (expectedBarcode && norm(boxType.barcode) === expectedBarcode) {
    return {kind: 'confirm_expected', boxType};
  }

  if (pendingSubstitution) {
    if (norm(pendingSubstitution.barcode) === norm(boxType.barcode)) {
      return {kind: 'substitution_confirm', boxType};
    }
    return {kind: 'substitution_switch', boxType};
  }

  return {kind: 'substitution_prompt', boxType};
}
